"use client"

import { useState, useEffect } from "react"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { Plus, Folder } from "lucide-react"
import { Project } from "@/lib/types"
import { getUserProjects } from "@/services/project-service"
import { CreateProjectDialog } from "./CreateProjectDialog"
import { useTranslation } from "react-i18next";

interface ProjectSelectorProps {
  selectedProjectId?: string | null
  onProjectSelect: (projectId: string | null) => void
  showCreateButton?: boolean
  label?: string
}

export function ProjectSelector({ 
  selectedProjectId, 
  onProjectSelect, 
  showCreateButton = true,
  label,
}: ProjectSelectorProps) {
  const { t, i18n } = useTranslation("common"); // Add translation hook
  const [projects, setProjects] = useState<Project[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    fetchProjects()
  }, [])

  const fetchProjects = async () => {
    setIsLoading(true)
    try {
      const projectData = await getUserProjects()
      setProjects(projectData)
    } catch (error) {
      console.error("Error fetching projects:", error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleProjectCreated = (newProject: Project) => {
    setProjects(prev => [newProject, ...prev])
    onProjectSelect(newProject.id)
  }

  return (
    <div className="grid gap-2">
      <Label>{label || t("common:selectProject.optionalProject")}</Label>
      <div className="flex gap-2">
        <Select 
          value={selectedProjectId || "none"} 
          onValueChange={(value) => onProjectSelect(value === "none" ? null : value)}
          disabled={isLoading}
        >
          <SelectTrigger className="flex-1">
            <SelectValue
              placeholder={
                isLoading
                  ? t("common:loading")
                  : t("common:selectProject.placeholder")
              }
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">
              <div
                dir={i18n.language === "ar" ? "rtl" : "ltr"}
                className={`flex items-center ${i18n.language === "ar" ? "flex-row-reverse" : ""}`}
              >
                <Folder
                  className={`${i18n.language === "ar" ? "ml-2" : "mr-2"} h-4 w-4 text-muted-foreground`}
                />
                {t("common:selectProject.noProject")}
              </div>
            </SelectItem>
            {projects.map((project) => (
              <SelectItem key={project.id} value={project.id}>
                <div className="flex items-center">
                  <Folder className="mr-2 h-4 w-4 text-muted-foreground" />
                  {project.name}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {showCreateButton && (
          <CreateProjectDialog 
            onProjectCreated={handleProjectCreated}
            trigger={
              <Button variant="outline" size="icon">
                <Plus className="h-4 w-4" />
              </Button>
            }
          />
        )}
      </div>
    </div>
  )
}
